import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../../components/Button/Button';
import styles from './Login.module.css';
import './SelectLoginOtpChannel.css';

/**
 * Channel Selection Screen for OTP Delivery
 * Allows user to choose EMAIL or SMS for receiving OTP code
 */
const SelectLoginOtpChannel = ({
  availableChannels = [],
  customerEmail = '',
  customerPhone = '',
  onChannelSelected = () => {},
  loading = false,
  error = null,
}) => {
  const channels = Array.isArray(availableChannels) ? availableChannels.filter(Boolean) : [];
  const canShowEmail = channels.includes('EMAIL') && Boolean(customerEmail);
  const canShowSms = channels.includes('SMS') && Boolean(customerPhone);
  const selectableChannels = [canShowEmail ? 'EMAIL' : null, canShowSms ? 'SMS' : null].filter(Boolean);
  const [selectedChannel, setSelectedChannel] = React.useState(selectableChannels[0] || null);

  React.useEffect(() => {
    if (!selectedChannel || !selectableChannels.includes(selectedChannel)) {
      setSelectedChannel(selectableChannels[0] || null);
    }
  }, [selectedChannel, selectableChannels]);

  if (!selectableChannels.length) {
    return (
      <div className="select-otp-channel-shell">
        <div className="select-otp-channel-panel">
          <h2 className="select-otp-channel-title">Verification required</h2>
          <p className="select-otp-channel-description">
            We could not find a valid email or phone number for OTP delivery. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = () => {
    if (selectedChannel && !loading) {
      onChannelSelected(selectedChannel);
    }
  };

  return (
    <div className="select-otp-channel-shell">
      <div className="select-otp-channel-panel">
        <h2 className="select-otp-channel-title">How would you like to receive your code?</h2>
        <p className="select-otp-channel-description">
          We&apos;ll send a verification code to confirm it&apos;s you
        </p>

        <div className="channel-form">
          {/* EMAIL OPTION */}
          {canShowEmail && (
            <label className="channel-option" htmlFor="otp-channel-email">
              <input
                id="otp-channel-email"
                type="radio"
                name="channel"
                value="EMAIL"
                checked={selectedChannel === 'EMAIL'}
                onChange={(e) => setSelectedChannel(e.target.value)}
                disabled={loading}
                className="channel-radio"
              />
              <div className="channel-option-content">
                <span className="channel-name">Email</span>
                <span className="channel-value">{customerEmail}</span>
              </div>
            </label>
          )}

          {/* SMS OPTION */}
          {canShowSms && (
            <label className="channel-option" htmlFor="otp-channel-sms">
              <input
                id="otp-channel-sms"
                type="radio"
                name="channel"
                value="SMS"
                checked={selectedChannel === 'SMS'}
                onChange={(e) => setSelectedChannel(e.target.value)}
                disabled={loading}
                className="channel-radio"
              />
              <div className="channel-option-content">
                <span className="channel-name">Text Message (SMS)</span>
                <span className="channel-value">{customerPhone}</span>
              </div>
            </label>
          )}

          {/* SUBMIT BUTTON */}
          <Button
            handleSubmit={handleSubmit}
            label={loading ? 'Sending Code...' : 'Send Code'}
            disabled={loading}
            isLoading={loading}
            customContainerStyles="send-code-button !block w-full bg-button text-white text-center py-2 h-11"
          />

          {/* ERROR MESSAGE */}
          {error && (
            <p className={styles.hasError}>{error}</p>
          )}
        </div>

      </div>
    </div>
  );
};

SelectLoginOtpChannel.propTypes = {
  availableChannels: PropTypes.arrayOf(PropTypes.oneOf(['EMAIL', 'SMS'])),
  customerEmail: PropTypes.string,
  customerPhone: PropTypes.string,
  onChannelSelected: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

SelectLoginOtpChannel.defaultProps = {
  availableChannels: [],
  customerEmail: '',
  customerPhone: '',
  onChannelSelected: () => {},
  loading: false,
  error: null,
};

export default SelectLoginOtpChannel;
